import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fmtMoney } from '../utils/orderUtils';
import ImagenProducto from './ImagenProducto';
import { decodeHtml } from '../utils/decodeHtml';
import { variationLabelFromItem } from '../utils/groupOrdersByProduct';
import useSalesChannels from '../hooks/useSalesChannels';
import useScrollLock from '../utils/useScrollLock';
import './OrderEditModal.css';

const API_URL    = process.env.REACT_APP_API_URL || 'http://localhost:3001';

let _newKeyCounter = 0;
function newKey() { return `new-${++_newKeyCounter}`; }

/* F3.2 (refactor de UI): el FORMULARIO se separa del overlay para poder vivir
   embebido en la página del pedido (Pedidos) — el modal clásico sigue siendo
   el default export para los callers que aún lo usan. La lógica de edición
   (incluido el PUT /modify auditado) NO se tocó: solo cambió el cascarón. */
export function OrderEditForm({ order, onClose, onSaved, embedded = false, registerControls = null }) {
  // ── Items state ─────────────────────────────────────────────────────────
  const [items, setItems] = useState(() =>
    order.line_items.map((i, idx) => ({
      ...i,
      _key:     `orig-${i.id ?? idx}`,
      _qty:     i.quantity,
      _deleted: false,
      _isNew:   false,
    }))
  );

  // Detect existing discount fee_line (negative total = manual discount)
  const existingFee = useMemo(() =>
    (order.fee_lines || []).find(f => parseFloat(f.total) < 0) || null
  , [order.fee_lines]);

  // ── Product search state ─────────────────────────────────────────────────
  const [searchQ,         setSearchQ]         = useState('');
  const [searchResults,   setSearchResults]   = useState([]);
  const [searchLoading,   setSearchLoading]   = useState(false);
  const [varPickerProd,   setVarPickerProd]   = useState(null);
  const searchWrapRef = useRef(null);

  // ── Payment / canal / shipping / discount ────────────────────────────────
  const [payMethod,       setPayMethod]       = useState(order.payment_method || '');
  const [payTitle,        setPayTitle]        = useState(order.payment_method_title || '');
  const canalInit = (order.meta_data || []).find(m => m.key === 'canal_venta')?.value || '';
  const { channels: oemChannels } = useSalesChannels({ includeArchived: true });
  const canalOpts = useMemo(() => oemChannels.map((c) => c.name), [oemChannels]);
  const [canalVenta,      setCanalVenta]      = useState(canalInit);
  const [canalCustom,     setCanalCustom]     = useState('');
  // Cuando cargan los canales, si el del pedido no está en la lista, tratarlo como "otro".
  useEffect(() => {
    if (!canalInit || !canalOpts.length) return;
    if (!canalOpts.includes(canalInit)) { setCanalCustom(canalInit); setCanalVenta(''); }
  }, [canalOpts, canalInit]);

  const origShippingAmt = parseFloat(order.shipping_total) || 0;
  const [shippingOn,      setShippingOn]      = useState(origShippingAmt > 0);
  const [shippingAmt,     setShippingAmt]     = useState(origShippingAmt > 0 ? String(origShippingAmt) : '');
  const [shippingTitle,   setShippingTitle]   = useState(order.shipping_lines?.[0]?.method_title || 'Envío a domicilio');

  const [discMode,        setDiscMode]        = useState(existingFee ? 'fixed' : null);
  const [discValue,       setDiscValue]       = useState(
    existingFee ? String(Math.round(Math.abs(parseFloat(existingFee.total)))) : ''
  );

  // ── UI ───────────────────────────────────────────────────────────────────
  const [gateways,        setGateways]        = useState([]);
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState(null);
  const [confirmOpen,     setConfirmOpen]     = useState(false);

  const sym = order.currency_symbol || '$';

  // Load payment gateways once
  useEffect(() => {
    fetch(`${API_URL}/api/pos/payment-gateways`)
      .then(r => r.json())
      .then(d => setGateways(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // Escape → close (solo como modal; embebido, Escape no debe cerrar la página)
  useEffect(() => {
    if (embedded) return undefined;
    const h = (e) => { if (e.key === 'Escape' && !confirmOpen) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, confirmOpen, embedded]);

  // Debounced product search
  useEffect(() => {
    if (searchQ.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/pos/search?q=${encodeURIComponent(searchQ.trim())}`);
        const d = await r.json();
        setSearchResults(d.products || []);
      } catch { setSearchResults([]); }
      finally   { setSearchLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ]);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!searchResults.length && !varPickerProd) return;
    const h = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchResults([]);
        setVarPickerProd(null);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [searchResults.length, varPickerProd]);

  // ── Computed values ──────────────────────────────────────────────────────
  const activeItems = items.filter(i => !i._deleted);

  /* Embebido (F3.2/F3.3): Guardar/Cancelar viven ARRIBA en el header de la
     página ("la página se vuelve editable") — el form registra sus controles
     al caller en vez de renderizar su propio footer. */
  useEffect(() => {
    if (!embedded || !registerControls) return undefined;
    registerControls({
      save: () => { setError(null); setConfirmOpen(true); },
      saving,
      canSave: activeItems.length > 0,
    });
    return () => registerControls(null);
  }, [embedded, registerControls, saving, activeItems.length]);
  const subtotal    = activeItems.reduce((s, i) => s + parseFloat(i.price || 0) * i._qty, 0);
  const shippingNum = shippingOn ? (parseFloat(shippingAmt) || 0) : 0;
  const discAmt     = discMode === 'pct'
    ? Math.round(subtotal * (parseFloat(discValue) || 0) / 100)
    : discMode === 'fixed' ? (parseFloat(discValue) || 0) : 0;
  const newTotal    = subtotal + shippingNum - discAmt;

  const effectiveCanal = canalOpts.includes(canalVenta) ? canalVenta : (canalCustom || canalVenta || '');

  // ── Item handlers ────────────────────────────────────────────────────────
  const changeQty = (key, delta) => {
    setItems(prev => prev.map(it => {
      if (it._key !== key || it._deleted) return it;
      const next = it._qty + delta;
      if (next < 1) return it;
      if (it._isNew && it._maxQty != null && next > it._maxQty) return it;
      return { ...it, _qty: next };
    }));
  };

  const deleteItem = (key) => {
    setItems(prev => prev.map(it => it._key === key ? { ...it, _deleted: true } : it));
  };

  // ── Add product ──────────────────────────────────────────────────────────
  const addProduct = (product, variation) => {
    const stock = variation ? (variation.stock_quantity ?? 0) : (product.stock_quantity ?? 0);
    if (stock <= 0) { setError('Sin stock disponible para ese producto.'); return; }
    const label = variation
      ? ((variation.attributes || []).map(a => a.option).join(' / ') || null)
      : null;
    setItems(prev => [...prev, {
      _key:         newKey(),
      id:           undefined,
      product_id:   product.id,
      variation_id: variation ? variation.id : null,
      name:         product.name,
      sku:          '',
      variationLabel: label,
      image:        product.image ? { src: product.image } : null,
      meta_data:    [],
      price:        parseFloat((variation?.price) || product.price) || 0,
      _qty:         1,
      _maxQty:      stock,
      _deleted:     false,
      _isNew:       true,
    }]);
    setSearchQ('');
    setSearchResults([]);
    setVarPickerProd(null);
    setError(null);
  };

  const handleSearchClick = (product) => {
    const vars = product.variations || [];
    if (vars.length === 0) {
      addProduct(product, null);
    } else if (vars.length === 1) {
      addProduct(product, vars[0]);
    } else {
      setVarPickerProd(product);
      setSearchResults([]);
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (activeItems.length === 0) {
      setError('El pedido debe tener al menos un producto.');
      setConfirmOpen(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const lineItems = activeItems.map(i => ({
        id:           i._isNew ? undefined : i.id,
        product_id:   i.product_id,
        variation_id: i.variation_id || 0,
        quantity:     i._qty,
        price:        i.price,
        name:         i.name || '',
      }));
      const removedIds = items.filter(i => i._deleted && !i._isNew).map(i => i.id);

      const payload = {
        line_items:               lineItems,
        removed_item_ids:         removedIds,
        payment_method:           payMethod,
        payment_method_title:     payTitle,
        canal_venta:              effectiveCanal,
        shipping_enabled:         shippingOn,
        shipping_amount:          shippingOn ? (parseFloat(shippingAmt) || 0) : 0,
        shipping_title:           shippingTitle,
        existing_shipping_id:     order.shipping_lines?.[0]?.id,
        discount_amount:          discAmt,
        discount_label:           discMode === 'pct' ? `${discValue}%` : '',
        existing_discount_fee_id: existingFee?.id,
      };

      const res  = await fetch(`${API_URL}/api/orders/${order.id}/modify`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al modificar el pedido');
      onSaved(data);
      onClose();
    } catch (err) {
      setError(err.message);
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
      <div className={`oem${embedded ? ' oem--embedded' : ''}`} onClick={e => e.stopPropagation()}>

        {/* Header — solo como modal (embebido: el header de la PÁGINA manda) */}
        {!embedded && (
          <div className="oem-header">
            <h2 className="oem-title">Modificar pedido #{order.number}</h2>
            <button className="oem-close" onClick={onClose} aria-label="Cerrar">✕</button>
          </div>
        )}

        <div className="oem-body">

          {/* ── Productos actuales ── */}
          <div className="oem-section">
            <div className="oem-section-title">Productos</div>
            <div className="oem-items">
              {activeItems.length === 0 && (
                <div className="oem-empty">Agrega al menos un producto.</div>
              )}
              {activeItems.map(item => {
                const varAttr = !item._isNew && !item.variationLabel
                  // C1: criterio unificado en el canónico; decodeHtml SE CONSERVA
                  // (el canónico devuelve crudo — pelarlo regresaría B5).
                  ? decodeHtml(variationLabelFromItem(item) || '')
                  : null;
                const varLabel = item.variationLabel || varAttr || null;
                return (
                  <div key={item._key} className="oem-item">
                    {item.image?.src
                      ? <ImagenProducto className="oem-item-thumb" src={item.image.src} srcset={item.image.srcset} sizes="miniatura" phWidth={40} alt="" />
                      : <div className="oem-item-thumb oem-item-thumb--ph" />}
                    <div className="oem-item-info">
                      <div className="oem-item-name">{item.name}</div>
                      {varLabel && <div className="oem-item-var">{varLabel}</div>}
                      <div className="oem-item-price">{fmtMoney(item.price, sym)} c/u</div>
                      {item._isNew && <span className="oem-item-new-tag">Nuevo</span>}
                    </div>
                    <div className="oem-item-controls">
                      <button
                        className="oem-qty-btn"
                        onClick={() => changeQty(item._key, -1)}
                        disabled={item._qty <= 1}
                      >−</button>
                      <span className="oem-qty-val">{item._qty}</span>
                      <button
                        className="oem-qty-btn"
                        onClick={() => changeQty(item._key, 1)}
                        disabled={item._isNew && item._maxQty != null && item._qty >= item._maxQty}
                      >+</button>
                      <span className="oem-item-sub">{fmtMoney(parseFloat(item.price) * item._qty, sym)}</span>
                      <button
                        className="oem-del-btn"
                        onClick={() => deleteItem(item._key)}
                        title="Eliminar este producto del pedido"
                      >✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Agregar producto ── */}
          <div className="oem-section">
            <div className="oem-section-title">Agregar producto</div>
            <div className="oem-search-wrap" ref={searchWrapRef}>
              <input
                className="oem-search-input"
                type="text"
                placeholder="Buscar por nombre de producto…"
                value={searchQ}
                onChange={e => { setSearchQ(e.target.value); setVarPickerProd(null); }}
                autoComplete="off"
              />
              {searchLoading && (
                <div className="oem-search-hint">Buscando…</div>
              )}
              {!searchLoading && searchQ.trim().length >= 2 && searchResults.length === 0 && (
                <div className="oem-search-hint">Sin resultados para "{searchQ}".</div>
              )}

              {searchResults.length > 0 && (
                <div className="oem-search-drop">
                  {searchResults.slice(0, 8).map(p => (
                    <button
                      key={p.id}
                      className="oem-search-item"
                      onClick={() => handleSearchClick(p)}
                    >
                      <div className="oem-search-img">
                        {p.image
                          ? <ImagenProducto src={p.image} srcset={p.imageSrcset} sizes="miniatura" phWidth={40} alt="" />
                          : <div className="oem-search-img-ph">SH</div>
                        }
                      </div>
                      <div className="oem-search-info">
                        <div className="oem-search-name">{p.name}</div>
                        <div className="oem-search-meta">
                          {p.price ? fmtMoney(p.price, sym) : '—'}
                          {' · '}
                          {(p.variations?.length ?? 0) > 0
                            ? `${p.variations.length} ${p.variations.length === 1 ? 'variación' : 'variaciones'}`
                            : `Stock: ${p.stock_quantity ?? 0}`
                          }
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {varPickerProd && (
                <div className="oem-var-picker">
                  <div className="oem-var-picker-title">
                    Variaciones de <b>{varPickerProd.name}</b>:
                  </div>
                  {varPickerProd.variations.map(v => {
                    const label = (v.attributes || []).map(a => a.option).join(' / ') || `#${v.id}`;
                    const stock = v.stock_quantity ?? 0;
                    return (
                      <button
                        key={v.id}
                        className={`oem-var-opt${stock <= 0 ? ' oem-var-opt--out' : ''}`}
                        disabled={stock <= 0}
                        onClick={() => addProduct(varPickerProd, v)}
                      >
                        <span className="oem-var-label">{label}</span>
                        <span className="oem-var-meta">
                          {fmtMoney(parseFloat(v.price || varPickerProd.price) || 0, sym)}
                          {' · '}
                          {stock > 0 ? `Stock: ${stock}` : 'Sin stock'}
                        </span>
                      </button>
                    );
                  })}
                  <button className="oem-var-cancel" onClick={() => { setVarPickerProd(null); setSearchQ(''); }}>
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Descuento y total — UNA tarjeta (F3.5: dos chips solos no
                justificaban tarjeta propia); el ticket del orden canónico ── */}
          <div className="oem-section oem-totals-section">
            <div className="oem-section-title">Descuento y total</div>
            <div className="oem-disc-btns">
              {[['pct', '% Porcentaje'], ['fixed', '$ Monto fijo']].map(([mode, label]) => (
                <button
                  key={mode}
                  className={`oem-disc-btn${discMode === mode ? ' oem-disc-btn--on' : ''}`}
                  onClick={() => { setDiscMode(discMode === mode ? null : mode); setDiscValue(''); }}
                >{label}</button>
              ))}
              {discMode && (
                <button className="oem-disc-clear" onClick={() => { setDiscMode(null); setDiscValue(''); }}>
                  × Quitar
                </button>
              )}
            </div>
            {discMode && (
              <div className="oem-disc-field">
                <input
                  className="oem-input oem-input--mt"
                  type="number"
                  min="0"
                  placeholder={discMode === 'pct' ? '% de descuento (ej. 10)' : 'Monto a descontar $'}
                  value={discValue}
                  onChange={e => setDiscValue(e.target.value)}
                  autoFocus
                />
                {discAmt > 0 && (
                  <div className="oem-disc-preview">
                    Descuento: {fmtMoney(discAmt, sym)}
                    {discMode === 'pct' && ` (${discValue}%)`}
                  </div>
                )}
              </div>
            )}
            <div className="oem-totals">
              <div className="oem-total-row">
                <span>Subtotal</span>
                <span>{fmtMoney(subtotal, sym)}</span>
              </div>
              {discAmt > 0 && (
                <div className="oem-total-row oem-total-disc">
                  <span>Descuento</span>
                  <span>−{fmtMoney(discAmt, sym)}</span>
                </div>
              )}
              {shippingOn && shippingNum > 0 && (
                <div className="oem-total-row">
                  <span>Envío</span>
                  <span>{fmtMoney(shippingNum, sym)}</span>
                </div>
              )}
              <div className="oem-total-grand">
                <span>Nuevo total</span>
                <span>{fmtMoney(Math.max(0, newTotal), sym)}</span>
              </div>
            </div>
          </div>
          {/* ── Pago y canal — UNA tarjeta (F3.5: ambas secciones cortas) ── */}
          <div className="oem-section">
            <div className="oem-section-title">Pago y canal</div>
            <div className="oem-sub-title">Método de pago</div>
            {gateways.length > 0 ? (
              <select
                className="oem-select"
                value={payMethod}
                onChange={e => {
                  const gw = gateways.find(g => g.id === e.target.value);
                  setPayMethod(e.target.value);
                  setPayTitle(gw?.title || e.target.value);
                }}
              >
                <option value="">— Sin cambios —</option>
                {gateways.map(g => (
                  <option key={g.id} value={g.id}>{g.title}</option>
                ))}
              </select>
            ) : (
              <input
                className="oem-input"
                type="text"
                value={payTitle || payMethod}
                onChange={e => { setPayMethod(e.target.value); setPayTitle(e.target.value); }}
                placeholder="Método de pago"
              />
            )}
            <div className="oem-sub-title oem-sub-title--mt">Canal de venta</div>
            <div className="oem-canal-row">
              {canalOpts.map(c => (
                <button
                  key={c}
                  className={`oem-canal-chip${canalVenta === c ? ' oem-canal-chip--on' : ''}`}
                  onClick={() => { setCanalVenta(c); setCanalCustom(''); }}
                >{c}</button>
              ))}
            </div>
            <input
              className="oem-input oem-input--mt"
              type="text"
              placeholder="Otro canal…"
              value={canalCustom}
              onChange={e => { setCanalCustom(e.target.value); setCanalVenta(''); }}
            />
          </div>

          {/* ── Cobro de envío (F3.5, frontera firmada por Miguel): este form
                es dueño del COBRO (afecta el total); la dirección, el contacto,
                la guía y el costo real viven en Gestión de envíos — una sola
                verdad de logística. Acá la dirección es SOLO LECTURA. ── */}
          <div className="oem-section">
            <div className="oem-section-title">Cobro de envío</div>
            <label className="oem-toggle-row">
              <span className="oem-toggle-label">Incluye envío</span>
              <input
                type="checkbox"
                className="oem-toggle-cb"
                checked={shippingOn}
                onChange={e => setShippingOn(e.target.checked)}
              />
            </label>
            {shippingOn && (
              <div className="oem-shipping-fields">
                <input
                  className="oem-input"
                  type="text"
                  placeholder="Descripción del envío"
                  value={shippingTitle}
                  onChange={e => setShippingTitle(e.target.value)}
                />
                <input
                  className="oem-input"
                  type="number"
                  min="0"
                  placeholder="Valor cobrado al cliente $"
                  value={shippingAmt}
                  onChange={e => setShippingAmt(e.target.value)}
                />
              </div>
            )}
            {(order.shipping?.address_1 || order.billing?.address_1) && (
              <div className="oem-envio-dir">
                <span className="oem-envio-dir-label">Dirección actual (solo lectura)</span>
                <span>{[
                  order.shipping?.address_1 || order.billing?.address_1,
                  order.shipping?.city || order.billing?.city,
                ].filter(Boolean).join(', ')}</span>
              </div>
            )}
            <p className="oem-envio-hint">
              La dirección, el contacto, la guía y el costo real se gestionan en <b>Gestión de envíos</b>.
            </p>
          </div>

          {error && <div className="oem-error">{error}</div>}
        </div>

        {/* Footer — solo como modal (embebido: Guardar/Cancelar arriba) */}
        {!embedded && (
          <div className="oem-footer">
            <button className="oem-btn-cancel" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button
              className="oem-btn-save"
              onClick={() => { setError(null); setConfirmOpen(true); }}
              disabled={saving || activeItems.length === 0}
            >
              Guardar cambios
            </button>
          </div>
        )}

        {/* Confirm dialog */}
        {confirmOpen && (
          <div className="oem-confirm-overlay" onClick={() => !saving && setConfirmOpen(false)}>
            <div className="oem-confirm" onClick={e => e.stopPropagation()}>
              <div className="oem-confirm-title">¿Confirmar modificación?</div>
              <div className="oem-confirm-body">
                Los cambios del pedido <b>#{order.number}</b> se aplicarán en WooCommerce y los ajustes de inventario se ejecutarán inmediatamente.
              </div>
              {error && <div className="oem-error">{error}</div>}
              <div className="oem-confirm-btns">
                <button
                  className="oem-btn-cancel"
                  onClick={() => setConfirmOpen(false)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  className="oem-btn-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Guardando…' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
  );
}

/* Cascarón modal clásico (overlay + scroll lock) sobre el formulario. */
function OrderEditModal(props) {
  useScrollLock();
  return (
    <div className="oem-overlay" onClick={props.onClose} role="dialog" aria-modal="true">
      <OrderEditForm {...props} />
    </div>
  );
}

export default OrderEditModal;
